import {
  type AskPunttAttachment,
  type AskPunttProjectData,
} from "@mg/schemas/src/christo/askPuntt";
import {
  CaretDown,
  CircleNotch,
  FileText,
  ImageSquare,
  VideoCamera,
} from "@phosphor-icons/react";
import { useState } from "react";

import { KeyFindingsSummarySection } from "./KeyFindingsSummarySection";

import { downloadAsset } from "../../../utils/http";
import { displayFileNameFromSource } from "../utils/projectContextDocuments";

function formatWorkflowLabel(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function fileTypeIcon(type: string) {
  if (type === "image") return ImageSquare;
  if (type === "video") return VideoCamera;
  return FileText;
}

function fileTypeLabel(type: string, name: string): string {
  const ext = name.split(".").pop()?.toUpperCase();
  if (ext && ext.length <= 5 && ext !== name.toUpperCase()) return ext;
  if (type === "image") return "IMAGE";
  if (type === "video") return "VIDEO";
  return "PDF";
}

function attachmentDisplayName(att: AskPunttAttachment): string {
  const trimmed = att.name?.trim();
  if (trimmed) return trimmed;
  return (
    displayFileNameFromSource(att.source) ??
    displayFileNameFromSource(att.key) ??
    "File"
  );
}

function attachmentDownloadSource(att: AskPunttAttachment): string {
  if (att.source?.trim()) return att.source.trim();
  if (att.key?.trim()) return `https://static.puntt.ai/${att.key.trim()}`;
  return "";
}

function ChatUploadedFileRow({
  attachment,
}: {
  attachment: AskPunttAttachment;
}) {
  const [downloading, setDownloading] = useState(false);
  const name = attachmentDisplayName(attachment);
  const source = attachmentDownloadSource(attachment);
  const TypeIcon = fileTypeIcon(attachment.type);
  const typeLabel = fileTypeLabel(attachment.type, name);
  const testId = `chat-uploaded-file-${attachment.key || name}`;

  const handleDownload = async () => {
    if (downloading || !source) return;
    setDownloading(true);
    try {
      await downloadAsset(source, name);
    } catch (err) {
      console.error("Failed to download chat upload", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={downloading || !source}
      data-testid={testId}
      aria-label={`Download ${name}`}
      className="flex w-full items-center gap-3 rounded-lg px-1 py-2.5 text-left transition-colors hover:text-puntt-neutral-gray-12 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center text-puntt-neutral-gray-11"
      >
        {downloading ? (
          <CircleNotch size={18} className="animate-spin" />
        ) : (
          <TypeIcon size={18} />
        )}
      </span>
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-puntt-neutral-gray-12">
        {name}
      </span>
      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-puntt-neutral-gray-10">
        {typeLabel}
      </span>
    </button>
  );
}

/**
 * Right-hand "CHAT DETAILS" panel for multi-ticket Ask Puntt browse mode.
 * Mirrors the project workspace `CampaignDocsPanel` chrome: expandable section
 * with a read-only chat summary (old sidebar header), key findings, and
 * uploaded files from the chat transcript.
 */
export function ChatDetailsPanel({
  title,
  workflowType,
  description,
  findingsSummary,
  ticketFindings,
  uploadedFiles = [],
  isKillerFlowStreaming = false,
  hasActiveStage = false,
  panelOpen: panelOpenProp,
  onPanelOpenChange,
}: {
  /** Chat title — mirrors the old multi-ticket sidebar header. */
  title: string;
  workflowType?: string;
  description?: string;
  findingsSummary?: string;
  ticketFindings?: AskPunttProjectData["ticketFindings"];
  /** User-uploaded files from chat message attachments. */
  uploadedFiles?: readonly AskPunttAttachment[];
  isKillerFlowStreaming?: boolean;
  hasActiveStage?: boolean;
  panelOpen?: boolean;
  onPanelOpenChange?: (open: boolean) => void;
}) {
  const [panelOpenInternal, setPanelOpenInternal] = useState(true);
  const panelOpen = panelOpenProp ?? panelOpenInternal;
  const setPanelOpen = (next: boolean) => {
    onPanelOpenChange?.(next);
    if (panelOpenProp === undefined) setPanelOpenInternal(next);
  };
  const [filesOpen, setFilesOpen] = useState(true);

  const workflowLabel = formatWorkflowLabel(workflowType ?? "");
  const descriptionText = description?.trim() ?? "";

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white ${panelOpen ? "h-full flex-1" : "shrink-0"}`}
      aria-label="Chat details"
      data-testid="chat-details-panel"
      data-panel-expanded={panelOpen ? "true" : "false"}
    >
      <div className="flex shrink-0 items-center gap-1 border-b border-puntt-neutral-gray-5 px-4 py-3">
        <button
          type="button"
          onClick={() => setPanelOpen(!panelOpen)}
          aria-expanded={panelOpen}
          aria-label="Toggle chat details section"
          data-testid="chat-details-toggle"
          className="flex min-w-0 flex-1 items-center gap-2 bg-base-white text-left"
        >
          <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
            Chat details
          </h2>
          <CaretDown
            size={14}
            aria-hidden="true"
            className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${panelOpen ? "" : "-rotate-90"}`}
          />
        </button>
      </div>

      {panelOpen && (
        <div
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-4 [scrollbar-width:thin]"
          data-testid="chat-details-scroll"
        >
          <div data-testid="chat-details-summary">
            <h3
              className="text-[17px] font-bold leading-snug text-puntt-neutral-gray-12"
              data-testid="chat-details-title"
            >
              {title}
            </h3>
            {workflowLabel ? (
              <p
                className="mt-0.5 text-[13px] text-puntt-neutral-gray-9"
                data-testid="chat-details-workflow-type"
              >
                {workflowLabel}
              </p>
            ) : null}
            {descriptionText ? (
              <p
                className="mt-1.5 text-[13px] leading-relaxed text-puntt-neutral-gray-10"
                data-testid="chat-details-description"
              >
                {descriptionText}
              </p>
            ) : null}
          </div>

          <div data-testid="chat-details-key-findings-section">
            <KeyFindingsSummarySection
              findingsSummary={findingsSummary}
              ticketFindings={ticketFindings}
              isKillerFlowStreaming={isKillerFlowStreaming}
              hasActiveStage={hasActiveStage}
            />
          </div>

          <div data-testid="chat-details-uploaded-files-section">
            <button
              type="button"
              onClick={() => setFilesOpen((o) => !o)}
              aria-expanded={filesOpen}
              aria-label="Toggle uploaded files"
              data-testid="chat-details-uploaded-files-toggle"
              className="flex w-full items-center gap-2 rounded-md bg-base-white p-2 text-left"
            >
              <h3 className="flex-1 text-[13px] font-bold text-puntt-neutral-gray-12">
                Uploaded files
              </h3>
              <CaretDown
                size={14}
                aria-hidden="true"
                className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${filesOpen ? "" : "-rotate-90"}`}
              />
            </button>

            {filesOpen && (
              <div
                className="mt-2 flex flex-col gap-2"
                data-testid="chat-details-uploaded-files-body"
              >
                {uploadedFiles.length === 0 ? (
                  <p
                    className="text-[13px] leading-relaxed text-puntt-neutral-gray-11"
                    data-testid="chat-details-uploaded-files-empty"
                  >
                    No files uploaded yet. Files you attach in this chat show up
                    here.
                  </p>
                ) : (
                  uploadedFiles.map((att) => (
                    <ChatUploadedFileRow
                      key={att.key || att.source}
                      attachment={att}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
